import React, { useContext, useMemo, useState } from 'react';
import { makeStyles, useTheme } from '@mui/styles';
import { useHistory } from 'react-router-dom';
import { Button, Grid, Toolbar, useMediaQuery } from '@mui/material';
import { colors } from '../../../../theme';
import { PATHS } from '../../../../../paths';
import { OnboardingContext } from '../OnboardingContext';
import { ButtonLoading } from '../../../../ButtonLoading';
import { useCurrentPath } from './useCurrentPath';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: colors.white,
    position: 'fixed',
    marginLeft: theme.spacing(-2),
    boxShadow: theme.shadows[4],
    zIndex: 100000,
    [theme.breakpoints.down('md')]: {
      top: 0,
      width: '100%',
    },
    [theme.breakpoints.up('md')]: {
      bottom: 0,
      width: 'calc(100% - 288px)',
    },
  },
  toolbar: {
    [theme.breakpoints.down('md')]: {
      minHeight: '48px',
    },
    [theme.breakpoints.up('md')]: {
      minHeight: '92px',
    },
  },
}));

const shouldShowBack = (path: string) => path !== PATHS.sp.onboarding.profile;

const getNextPath = (currentPath: string) => {
  switch (currentPath) {
    case PATHS.sp.onboarding.profile:
      return PATHS.sp.onboarding.key_contacts;
    case PATHS.sp.onboarding.key_contacts:
      return PATHS.sp.onboarding.services;
    case PATHS.sp.onboarding.services:
      return PATHS.sp.onboarding.diversity;
    case PATHS.sp.onboarding.diversity:
      return PATHS.sp.onboarding.documents;
    case PATHS.sp.onboarding.documents:
      return PATHS.sp.main;
    default: return null;
  }
};

export const FooterToolbar = () => {
  const history = useHistory();
  const boardingCtx = useContext(OnboardingContext);
  const [loading, setLoading] = useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const classes = useStyles();

  const currentPath = useCurrentPath();

  const showBack = useMemo(() => shouldShowBack(currentPath), [currentPath]);
  const nextPath = useMemo(() => getNextPath(currentPath), [currentPath]);

  const validateFormAndGoTo = async (path: string) => {
    const { dirty, validateForm, submitForm } = boardingCtx;
    if (dirty && validateForm && submitForm) {
      setLoading(true);
      const errors = await validateForm();
      if (!Object.keys(errors).length) {
        const response = await submitForm();
        if (response && !response?.errors?.length) {
          history.push(path);
        }
      } else {
        await submitForm();
      }
      setLoading(false);
    } else if (validateForm && submitForm) {
      const errors = await validateForm();
      if (!Object.keys(errors).length) {
        history.push(path);
      } else {
        await submitForm();
      }
    } else {
      history.push(path);
    }
  };

  return (
    <div className={classes.root}>
      <Toolbar classes={{ root: classes.toolbar }}>
        <Grid container justifyContent="space-between" flex={1}>
          <Grid container spacing={1}>
            <Grid>
              {showBack ? (
                <Button
                  variant="outlined"
                  color="secondary"
                  size={isDesktop ? 'large' : 'small'}
                  onClick={() => history.goBack()}
                >
                  Back
                </Button>
              ) : null}
            </Grid>
            <Grid>
              {nextPath ? (
                <Button
                  color="secondary"
                  size={isDesktop ? 'large' : 'small'}
                  onClick={async () => validateFormAndGoTo(nextPath)}
                  disabled={loading}
                >
                  <ButtonLoading loading={loading} />
                  {boardingCtx?.dirty && boardingCtx?.submitForm ? 'Save & ' : ''}
                  Continue
                </Button>
              ) : null}
            </Grid>
          </Grid>
          <Grid>
            <Button
              variant="outlined"
              disabled={loading}
              size={isDesktop ? 'large' : 'small'}
              onClick={() => validateFormAndGoTo(PATHS.sp.main)}
            >
              <ButtonLoading loading={loading} />
              {boardingCtx?.dirty && boardingCtx?.submitForm ? 'Save & ' : ''}
              Finish Later
            </Button>
          </Grid>
        </Grid>
      </Toolbar>
    </div>
  );
};
